/**
 * Whether the last trip to the login screen was an interrupted session.
 *
 * The login screen used to infer this from the route it was redirected from:
 * any `from` that was not the dashboard meant "expired". That was wrong. The
 * route guard attaches `from` to *every* redirect, including the first visit of
 * someone who has never signed in, so the site root produced `from === '/'` and
 * a first-time visitor was told their session had ended.
 *
 * "Ended" is not something the login screen can work out from a URL. It is a
 * fact known only where a 401 arrives while somebody was signed in, so it is
 * recorded at both places that can see one:
 *
 *   - app/providers.tsx, for a 401 from any ordinary query or mutation;
 *   - features/auth/api.ts, for GET /auth/me, which swallows its own 401 and so
 *     never reaches the cache-level handler at all.
 *
 * Module state rather than sessionStorage: the redirect that follows an expiry
 * is a client-side navigation, so this survives it, and nothing should outlive
 * a full page load — after a reload there is no interrupted session to explain,
 * only a sign-in screen.
 */
let ended = false;

/**
 * Record that a session was interrupted.
 *
 * Callers must check first that somebody was actually signed in. A 401 on its
 * own is not an expiry: a failed sign-in is answered 401 by design.
 */
export function markSessionEnded(): void {
  ended = true;
}

/**
 * Read the flag WITHOUT clearing it.
 *
 * Kept separate from clearing on purpose. This is read in a `useState`
 * initialiser, and the app runs under `<StrictMode>`, which double-invokes
 * those in development to surface exactly this kind of impurity — a
 * read-and-clear would return true on the first invocation and false on the
 * second, so the banner would behave differently in dev and in production.
 */
export function sessionEndedPending(): boolean {
  return ended;
}

/**
 * Clear the flag, so the banner does not reappear on the next visit to the
 * login screen. Called from an effect after the value has been read, and
 * idempotent, since StrictMode runs effects twice in development.
 */
export function clearSessionEnded(): void {
  ended = false;
}
